import React from 'react';
import {
  View,
  Text,
  Image,
  TouchableWithoutFeedback,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import {TemplateProps} from '../types';
import {styles} from './styles';
import {MATERIAL_ICONS_FONT} from '../../styles/fonts';

import {Reminder} from '../../types';

/**
 * Parse a reminder and get local date/time info
 */
interface ParsedReminder {
  reminder: Reminder;
  localDate: string;  // YYYY-MM-DD in local timezone
  localTime: string;  // HH:mm in local timezone, or '--:--' for all-day
  dateLabel: string;  // "Today", "Tomorrow", or formatted date
  isToday: boolean;
}

function parseReminder(reminder: Reminder, t: (key: string) => string): ParsedReminder {
  let localDate: string;
  let localTime: string;

  if (reminder.isAllDay || !reminder.startTime) {
    // All-day event - use the date as-is
    localDate = reminder.date;
    localTime = '--:--';
  } else {
    // Timed event - parse UTC and convert to local timezone
    const utcDate = new Date(reminder.startTime);
    localDate = utcDate.toLocaleDateString('sv-SE'); // YYYY-MM-DD format
    localTime = utcDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  // Determine date label
  const todayStr = new Date().toLocaleDateString('sv-SE');
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toLocaleDateString('sv-SE');

  let dateLabel: string;
  let isToday = false;

  if (localDate === todayStr) {
    dateLabel = t('frame.today');
    isToday = true;
  } else if (localDate === tomorrowStr) {
    dateLabel = t('frame.tomorrow');
  } else {
    const date = new Date(localDate + 'T12:00:00');
    dateLabel = date.toLocaleDateString(undefined, {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  }

  return {
    reminder,
    localDate,
    localTime,
    dateLabel,
    isToday,
  };
}

/**
 * Get icon for reminder based on title keywords
 */
function getReminderIcon(title: string): string {
  const lower = title.toLowerCase();
  if (lower.includes('medic') || lower.includes('pill') || lower.includes('drug')) return 'medication';
  if (lower.includes('doctor') || lower.includes('dr.') || lower.includes('checkup') || lower.includes('appointment')) return 'medical_services';
  if (lower.includes('soccer') || lower.includes('football') || lower.includes('sport')) return 'sports_soccer';
  if (lower.includes('dinner') || lower.includes('lunch') || lower.includes('breakfast') || lower.includes('restaurant')) return 'restaurant';
  if (lower.includes('garden')) return 'yard';
  if (lower.includes('birthday') || lower.includes('party')) return 'celebration';
  if (lower.includes('call') || lower.includes('phone')) return 'call';
  if (lower.includes('shop') || lower.includes('grocery') || lower.includes('store')) return 'shopping_cart';
  if (lower.includes('therapy') || lower.includes('physio')) return 'physical_therapy';
  return 'event';
}

/**
 * Classic Template Component
 *
 * The original frame design with:
 * - Full-screen photo background with pan/zoom
 * - Gradient overlays for text readability
 * - Portrait: stacked panels for time and reminders
 * - Landscape: time on left, reminders panel on right
 */
export default function ClassicTemplate(props: TemplateProps) {
  const {
    currentPhotoUrl,
    previousPhotoUrl,
    isTransitioning,
    scaledImageSize,
    currentOffset,
    previousOffset,
    screenWidth,
    screenHeight,
    isPortrait,
    formattedTime,
    formattedDate,
    greeting,
    reminders,
    isEditMode,
    showSettings,
    showHint,
    isSaving,
    showSaved,
    loadingProgress,
    panOffset,
    onSettingsPress,
    onEditPress,
    onExitEditMode,
    onDismissHint,
    onDragStart,
    onDragMove,
    onDragEnd,
    panResponderHandlers,
    t,
    fontScale = 1.0,
    displayDeviceName,
  } = props;

  // Dynamic font sizes based on fontScale
  const scaledFonts = {
    greeting: 32 * fontScale,
    time: 80 * fontScale,
    date: 28 * fontScale,
    reminderTitle: 16 * fontScale,
    reminderTitleToday: 18 * fontScale,
    reminderDate: 12 * fontScale,
    reminderTime: 11 * fontScale,
    reminderCalendar: 12 * fontScale,
    reminderHeaderTitle: 20 * fontScale,
    // Portrait sizes
    portraitGreeting: 22 * fontScale,
    portraitTime: 56 * fontScale,
    portraitDate: 20 * fontScale,
  };

  // Portrait layout - photo background with stacked overlays
  if (isPortrait) {
    return (
      <View style={styles.container}>
        {/* Photo Background with Crossfade */}
        <View style={styles.photoContainer}>
          {/* Previous photo (stays visible during transition, behind current) */}
          {isTransitioning && previousPhotoUrl && (
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
              }}>
              <Image
                source={{uri: previousPhotoUrl}}
                style={[
                  styles.photo,
                  {
                    width: scaledImageSize.width,
                    height: scaledImageSize.height,
                    marginLeft: -(scaledImageSize.width - screenWidth) / 2 + previousOffset.x,
                    marginTop: -(scaledImageSize.height - screenHeight) / 2 + previousOffset.y,
                  },
                ]}
                resizeMode="cover"
              />
            </View>
          )}
          {/* Current photo (fades in over previous) */}
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              animation: isTransitioning ? 'fadeIn 0.8s ease-in-out' : 'none',
            } as any}>
            <Image
              source={{uri: currentPhotoUrl}}
              style={[
                styles.photo,
                {
                  width: scaledImageSize.width,
                  height: scaledImageSize.height,
                  marginLeft: -(scaledImageSize.width - screenWidth) / 2 + (isEditMode ? panOffset.x : currentOffset.x),
                  marginTop: -(scaledImageSize.height - screenHeight) / 2 + (isEditMode ? panOffset.y : currentOffset.y),
                },
              ]}
              resizeMode="cover"
            />
          </View>
          {!isEditMode && <View style={styles.gradientTop} />}
          {!isEditMode && <View style={styles.portraitGradientBottom} />}
        </View>

        {/* Edit mode gesture overlay - captures all pan gestures */}
        {isEditMode && (
          <>
            {/* Pan gesture area - excludes header and footer */}
            <View
              style={{
                position: 'absolute',
                top: 80,
                left: 0,
                right: 0,
                bottom: 100,
                zIndex: 80,
                cursor: 'grab',
                touchAction: 'none',
                userSelect: 'none',
              } as any}
              {...(Platform.OS === 'web' ? {
                onMouseDown: onDragStart,
                onMouseMove: onDragMove,
                onMouseUp: onDragEnd,
                onMouseLeave: onDragEnd,
                onTouchStart: onDragStart,
                onTouchMove: onDragMove,
                onTouchEnd: onDragEnd,
              } : panResponderHandlers)}
            />
            {/* Visual elements - not capturing gestures */}
            <View style={styles.editModeBorder} pointerEvents="none" />
            <View style={[styles.editModeHeader, {zIndex: 81}]} pointerEvents="box-none">
              <Text style={styles.editModeText}>{t('frame.dragToReposition')}</Text>
              {isSaving && <Text style={styles.editModeSaving}>{t('common.saving')}</Text>}
              {showSaved && <Text style={styles.editModeSaved}>{t('common.saved')}</Text>}
            </View>
            <View style={[styles.editModeFooter, {zIndex: 81}]}>
              <TouchableOpacity
                style={styles.editModeDoneButton}
                onPress={onExitEditMode}
                activeOpacity={0.8}>
                <Text style={styles.editModeDoneIcon}>check</Text>
                <Text style={styles.editModeDoneText}>{t('common.done')}</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {/* Settings Button - Top Right */}
        {showSettings && !isEditMode && (
          <>
            {/* Display name label - Top Left */}
            {displayDeviceName && (
              <View style={styles.displayNameLabel}>
                <Text style={styles.displayNameIcon}>tv</Text>
                <Text style={styles.displayNameText}>{displayDeviceName}</Text>
              </View>
            )}
            <TouchableOpacity
              style={styles.settingsButton}
              onPress={onSettingsPress}
              activeOpacity={0.7}>
              <Text style={styles.settingsIcon}>settings</Text>
            </TouchableOpacity>
            {/* Reposition button */}
            <TouchableOpacity
              style={styles.repositionButton}
              onPress={onEditPress}
              activeOpacity={0.7}>
              <Text style={styles.settingsIcon}>pan_tool</Text>
            </TouchableOpacity>
          </>
        )}

        {/* Loading progress bar - top left (only when not showing settings) */}
        {!showSettings && loadingProgress && loadingProgress.loaded < loadingProgress.total && (
          <View style={styles.loadingProgressContainer}>
            <View style={styles.loadingProgressBar}>
              <View
                style={[
                  styles.loadingProgressFill,
                  {width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%`},
                ]}
              />
            </View>
            <Text style={styles.loadingProgressText}>
              {loadingProgress.loaded}/{loadingProgress.total}
            </Text>
          </View>
        )}

        {/* First-time hint */}
        {showHint && (
          <View style={styles.hintOverlay}>
            <View style={styles.hintContainer}>
              {/* Arrow pointing to settings button location */}
              <View style={styles.hintArrow}>
                <Text style={styles.hintArrowIcon}>north_east</Text>
              </View>
              <View style={styles.hintBubble}>
                <Text style={styles.hintIcon}>touch_app</Text>
                <View style={styles.hintContent}>
                  <Text style={styles.hintText}>{t('frame.tapToReveal')}</Text>
                  <Text style={styles.hintSubtext}>{t('frame.settingsButtonHere')}</Text>
                </View>
              </View>
              <TouchableOpacity
                style={styles.hintButton}
                onPress={onDismissHint}
                activeOpacity={0.8}>
                <Text style={styles.hintButtonText}>{t('common.understood')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Content overlay - hidden in edit mode */}
        {!isEditMode && (
        <View style={styles.portraitOverlay}>
          {/* DateTime Panel */}
          <View style={styles.portraitDateTimePanel}>
            <Text style={[styles.portraitGreeting, {fontSize: scaledFonts.portraitGreeting}]}>{greeting}</Text>
            <Text style={[styles.portraitTime, {fontSize: scaledFonts.portraitTime, lineHeight: scaledFonts.portraitTime * 1.1}]}>{formattedTime}</Text>
            <Text style={[styles.portraitDate, {fontSize: scaledFonts.portraitDate}]}>{formattedDate.short}</Text>
          </View>

          {/* Reminders Panel */}
          {reminders.length > 0 && (
            <View style={styles.portraitRemindersPanel}>
              <View style={styles.reminderHeader}>
                <View style={styles.reminderHeaderLeft}>
                  <View style={styles.reminderIconBg}>
                    <Text style={styles.reminderHeaderIcon}>event_upcoming</Text>
                  </View>
                  <Text style={[styles.reminderHeaderTitle, {fontSize: scaledFonts.reminderHeaderTitle}]}>{t('frame.reminders')}</Text>
                </View>
              </View>

              <ScrollView
                style={styles.portraitRemindersList}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.remindersListContent}>
                {reminders.slice(0, 5).map((reminder) => {
                  const parsed = parseReminder(reminder, t);
                  const icon = getReminderIcon(reminder.title);

                  return (
                    <View
                      key={reminder.id}
                      style={[
                        styles.reminderItem,
                        parsed.isToday && styles.reminderItemToday,
                      ]}>
                      <View style={[
                        styles.reminderDateSection,
                        parsed.isToday && styles.reminderDateSectionToday,
                      ]}>
                        <Text style={[
                          styles.reminderDateLabel,
                          parsed.isToday && styles.reminderDateLabelToday,
                          {fontSize: scaledFonts.reminderDate},
                        ]}>
                          {parsed.dateLabel}
                        </Text>
                        <Text style={[styles.reminderTime, {fontSize: scaledFonts.reminderTime}]}>
                          {parsed.localTime}
                        </Text>
                      </View>
                      <View style={styles.reminderContent}>
                        <Text style={[
                          styles.reminderTitle,
                          parsed.isToday && styles.reminderTitleToday,
                          {fontSize: parsed.isToday ? scaledFonts.reminderTitleToday : scaledFonts.reminderTitle},
                        ]}>
                          {reminder.title}
                        </Text>
                        {reminder.calendarName && (
                          <View style={styles.reminderMeta}>
                            <Text style={[
                              styles.reminderIcon,
                              parsed.isToday && styles.reminderIconToday,
                            ]}>
                              {icon}
                            </Text>
                            <Text style={[styles.reminderCalendar, {fontSize: scaledFonts.reminderCalendar}]}>
                              {reminder.calendarName}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>
                  );
                })}
              </ScrollView>
            </View>
          )}
        </View>
        )}
      </View>
    );
  }

  // Landscape layout - original design
  return (
    <View style={styles.container}>
      {/* Photo Background with Crossfade */}
      <View style={styles.photoContainer}>
        {/* Previous photo (stays visible during transition, behind current) */}
        {isTransitioning && previousPhotoUrl && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
            }}>
            <Image
              source={{uri: previousPhotoUrl}}
              style={[
                styles.photo,
                {
                  width: scaledImageSize.width,
                  height: scaledImageSize.height,
                  marginLeft: -(scaledImageSize.width - screenWidth) / 2 + previousOffset.x,
                  marginTop: -(scaledImageSize.height - screenHeight) / 2 + previousOffset.y,
                },
              ]}
              resizeMode="cover"
            />
          </View>
        )}
        {/* Current photo (fades in over previous) */}
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            animation: isTransitioning ? 'fadeIn 0.8s ease-in-out' : 'none',
          } as any}>
          <Image
            source={{uri: currentPhotoUrl}}
            style={[
              styles.photo,
              {
                width: scaledImageSize.width,
                height: scaledImageSize.height,
                marginLeft: -(scaledImageSize.width - screenWidth) / 2 + (isEditMode ? panOffset.x : currentOffset.x),
                marginTop: -(scaledImageSize.height - screenHeight) / 2 + (isEditMode ? panOffset.y : currentOffset.y),
              },
            ]}
            resizeMode="cover"
          />
        </View>
        {/* Gradient overlays */}
        {!isEditMode && <View style={styles.gradientTop} />}
        {!isEditMode && <View style={styles.gradientBottom} />}
      </View>

      {/* Edit mode gesture overlay - captures all pan gestures */}
      {isEditMode && (
        <>
          {/* Pan gesture area - excludes header and footer */}
          <View
            style={{
              position: 'absolute',
              top: 80,
              left: 0,
              right: 0,
              bottom: 100,
              zIndex: 80,
              cursor: 'grab',
              touchAction: 'none',
              userSelect: 'none',
            } as any}
            {...(Platform.OS === 'web' ? {
              onMouseDown: onDragStart,
              onMouseMove: onDragMove,
              onMouseUp: onDragEnd,
              onMouseLeave: onDragEnd,
              onTouchStart: onDragStart,
              onTouchMove: onDragMove,
              onTouchEnd: onDragEnd,
            } : panResponderHandlers)}
          />
          {/* Visual elements - not capturing gestures */}
          <View style={styles.editModeBorder} pointerEvents="none" />
          <View style={[styles.editModeHeader, {zIndex: 81}]} pointerEvents="box-none">
            <Text style={styles.editModeText}>{t('frame.dragToReposition')}</Text>
            {isSaving && <Text style={styles.editModeSaving}>{t('common.saving')}</Text>}
            {showSaved && <Text style={styles.editModeSaved}>{t('common.saved')}</Text>}
          </View>
          <View style={[styles.editModeFooter, {zIndex: 81}]}>
            <TouchableOpacity
              style={styles.editModeDoneButton}
              onPress={onExitEditMode}
              activeOpacity={0.8}>
              <Text style={styles.editModeDoneIcon}>check</Text>
              <Text style={styles.editModeDoneText}>{t('common.done')}</Text>
            </TouchableOpacity>
          </View>
        </>
      )}

      {/* Settings Button - Top Right */}
      {showSettings && !isEditMode && (
        <>
          {/* Display name label - Top Left */}
          {displayDeviceName && (
            <View style={styles.displayNameLabel}>
              <Text style={styles.displayNameIcon}>tv</Text>
              <Text style={styles.displayNameText}>{displayDeviceName}</Text>
            </View>
          )}
          <TouchableOpacity
            style={styles.settingsButton}
            onPress={onSettingsPress}
            activeOpacity={0.7}>
            <Text style={styles.settingsIcon}>settings</Text>
          </TouchableOpacity>
          {/* Reposition button */}
          <TouchableOpacity
            style={styles.repositionButton}
            onPress={onEditPress}
            activeOpacity={0.7}>
            <Text style={styles.settingsIcon}>pan_tool</Text>
          </TouchableOpacity>
        </>
      )}

      {/* Loading progress bar - top left (only when not showing settings) */}
      {!showSettings && loadingProgress && loadingProgress.loaded < loadingProgress.total && (
        <View style={styles.loadingProgressContainer}>
          <View style={styles.loadingProgressBar}>
            <View
              style={[
                styles.loadingProgressFill,
                {width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%`},
              ]}
            />
          </View>
          <Text style={styles.loadingProgressText}>
            {loadingProgress.loaded}/{loadingProgress.total}
          </Text>
        </View>
      )}

      {/* First-time hint */}
      {showHint && (
        <View style={styles.hintOverlay}>
          <View style={styles.hintContainer}>
            {/* Arrow pointing to settings button location */}
            <View style={styles.hintArrow}>
              <Text style={styles.hintArrowIcon}>north_east</Text>
            </View>
            <View style={styles.hintBubble}>
              <Text style={styles.hintIcon}>touch_app</Text>
              <View style={styles.hintContent}>
                <Text style={styles.hintText}>{t('frame.tapToReveal')}</Text>
                <Text style={styles.hintSubtext}>{t('frame.settingsButtonHere')}</Text>
              </View>
            </View>
            <TouchableOpacity
              style={styles.hintButton}
              onPress={onDismissHint}
              activeOpacity={0.8}>
              <Text style={styles.hintButtonText}>{t('common.understood')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Main Info - Bottom Left - hidden in edit mode */}
      {!isEditMode && (
        <View style={styles.mainInfo}>
          <Text style={[styles.greeting, {fontSize: scaledFonts.greeting}]}>{greeting}</Text>
          <View style={styles.timeRow}>
            <Text style={[styles.time, {fontSize: scaledFonts.time, lineHeight: scaledFonts.time}]}>{formattedTime}</Text>
            <Text style={[styles.date, {fontSize: scaledFonts.date}]}>{formattedDate.short}</Text>
          </View>
        </View>
      )}

      {/* Reminders Panel - Bottom Right - hidden in edit mode */}
      {!isEditMode && reminders.length > 0 && (
        <View style={styles.remindersPanel}>
          {/* Panel Header */}
          <View style={styles.reminderHeader}>
            <View style={styles.reminderHeaderLeft}>
              <View style={styles.reminderIconBg}>
                <Text style={styles.reminderHeaderIcon}>event_upcoming</Text>
              </View>
              <Text style={[styles.reminderHeaderTitle, {fontSize: scaledFonts.reminderHeaderTitle}]}>{t('frame.reminders')}</Text>
            </View>
          </View>

          {/* Reminders List */}
          <ScrollView
            style={styles.remindersList}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.remindersListContent}>
            {reminders.slice(0, 5).map((reminder) => {
              const parsed = parseReminder(reminder, t);
              const icon = getReminderIcon(reminder.title);

              return (
                <View
                  key={reminder.id}
                  style={[
                    styles.reminderItem,
                    parsed.isToday && styles.reminderItemToday,
                  ]}>
                  <View style={[
                    styles.reminderDateSection,
                    parsed.isToday && styles.reminderDateSectionToday,
                  ]}>
                    <Text style={[
                      styles.reminderDateLabel,
                      parsed.isToday && styles.reminderDateLabelToday,
                      {fontSize: scaledFonts.reminderDate},
                    ]}>
                      {parsed.dateLabel}
                    </Text>
                    <Text style={[styles.reminderTime, {fontSize: scaledFonts.reminderTime}]}>
                      {parsed.localTime}
                    </Text>
                  </View>
                  <View style={styles.reminderContent}>
                    <Text style={[
                      styles.reminderTitle,
                      parsed.isToday && styles.reminderTitleToday,
                      {fontSize: parsed.isToday ? scaledFonts.reminderTitleToday : scaledFonts.reminderTitle},
                    ]}>
                      {reminder.title}
                    </Text>
                    {reminder.calendarName && (
                      <View style={styles.reminderMeta}>
                        <Text style={[
                          styles.reminderIcon,
                          parsed.isToday && styles.reminderIconToday,
                        ]}>
                          {icon}
                        </Text>
                        <Text style={[styles.reminderCalendar, {fontSize: scaledFonts.reminderCalendar}]}>
                          {reminder.calendarName}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
}
